/**
 * Spending per category and group, with budget vs actual, over one or more accounting months.
 * Mirrors the monthly sheets' Budget / Spese Effettive / Differenza columns (difference =
 * budget − actual, positive when under budget).
 */
import type { CostNature, YearMonth } from "@/domain/model";
import { Decimal, toDecimalString, type DecimalString } from "@/domain/money";
import type { FinanceIndex } from "./data";

export interface CategoryLine {
  categoryId: string;
  label: string;
  groupId: string;
  costNature: CostNature | null;
  actual: DecimalString;
  budget: DecimalString;
  /** budget − actual */
  variance: DecimalString;
  /** actual / budget, or null when there is no budget */
  usedRatio: DecimalString | null;
  count: number;
}

export interface GroupLine {
  groupId: string;
  label: string;
  actual: DecimalString;
  budget: DecimalString;
  variance: DecimalString;
  usedRatio: DecimalString | null;
  categories: CategoryLine[];
}

export interface NatureSplit {
  fixed: DecimalString;
  discretionary: DecimalString;
  unclassified: DecimalString;
}

function ratio(actual: Decimal, budget: Decimal): DecimalString | null {
  return budget.gt(0) ? toDecimalString(actual.div(budget)) : null;
}

/**
 * All categories that have spending or a budget in the given months, grouped in Excel order.
 * Categories with neither are omitted unless `includeEmpty` is set.
 */
export function categoryBreakdown(index: FinanceIndex, months: YearMonth[], { includeEmpty = false } = {}): GroupLine[] {
  const actual = new Map<string, Decimal>();
  const counts = new Map<string, number>();
  const budget = new Map<string, Decimal>();
  for (const month of months) {
    for (const e of index.expensesByMonth.get(month) ?? []) {
      actual.set(e.categoryId, (actual.get(e.categoryId) ?? new Decimal(0)).plus(e.amountEur));
      counts.set(e.categoryId, (counts.get(e.categoryId) ?? 0) + 1);
    }
    for (const [categoryId, amount] of index.budgetsByMonth.get(month) ?? []) {
      budget.set(categoryId, (budget.get(categoryId) ?? new Decimal(0)).plus(amount));
    }
  }

  const groups = [...index.groups.values()].sort((a, b) => a.sortOrder - b.sortOrder);
  const categories = [...index.categories.values()].sort((a, b) => a.sortOrder - b.sortOrder);
  const lines: GroupLine[] = [];
  for (const group of groups) {
    const catLines: CategoryLine[] = [];
    let gActual = new Decimal(0);
    let gBudget = new Decimal(0);
    for (const c of categories.filter((x) => x.groupId === group.id)) {
      const a = actual.get(c.id) ?? new Decimal(0);
      const b = budget.get(c.id) ?? new Decimal(0);
      if (!includeEmpty && a.isZero() && b.isZero()) continue;
      gActual = gActual.plus(a);
      gBudget = gBudget.plus(b);
      catLines.push({
        categoryId: c.id,
        label: c.excelLabel,
        groupId: group.id,
        costNature: c.costNature,
        actual: toDecimalString(a),
        budget: toDecimalString(b),
        variance: toDecimalString(b.minus(a)),
        usedRatio: ratio(a, b),
        count: counts.get(c.id) ?? 0,
      });
    }
    if (catLines.length === 0 && !includeEmpty) continue;
    lines.push({
      groupId: group.id,
      label: group.excelLabel,
      actual: toDecimalString(gActual),
      budget: toDecimalString(gBudget),
      variance: toDecimalString(gBudget.minus(gActual)),
      usedRatio: ratio(gActual, gBudget),
      categories: catLines,
    });
  }
  return lines;
}

export function natureSplit(index: FinanceIndex, months: YearMonth[]): NatureSplit {
  let fixed = new Decimal(0);
  let discretionary = new Decimal(0);
  let unclassified = new Decimal(0);
  for (const month of months) {
    for (const e of index.expensesByMonth.get(month) ?? []) {
      const nature = index.categories.get(e.categoryId)?.costNature ?? null;
      if (nature === "fixed") fixed = fixed.plus(e.amountEur);
      else if (nature === "discretionary") discretionary = discretionary.plus(e.amountEur);
      else unclassified = unclassified.plus(e.amountEur);
    }
  }
  return { fixed: toDecimalString(fixed), discretionary: toDecimalString(discretionary), unclassified: toDecimalString(unclassified) };
}

export function monthBudgetTotal(index: FinanceIndex, month: YearMonth): Decimal {
  let total = new Decimal(0);
  for (const amount of index.budgetsByMonth.get(month)?.values() ?? []) total = total.plus(amount);
  return total;
}
